import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Wind, CheckCircle2, ArrowRight, Activity, HeartHandshake } from 'lucide-react';

export const Landing = () => {
  const { t, language } = useLanguage();
  const { isAuthenticated } = useAuth();

  const features = [
    {
      icon: Activity,
      title_fr: 'Test Fagerström',
      title_en: 'Fagerström Test',
      desc_fr: 'Évaluez votre dépendance en 6 questions scientifiques',
      desc_en: 'Assess your dependence with 6 scientific questions'
    },
    {
      icon: CheckCircle2,
      title_fr: 'Suivi hebdomadaire',
      title_en: 'Weekly tracking',
      desc_fr: 'Regardez votre niveau d\'anxiété et d\'envie évoluer en passant un test hebdomadaire de 5 questions',
      desc_en: 'Watch your anxiety and craving levels evolve by taking a weekly 5-question test'
    },
    {
      icon: HeartHandshake,
      title_fr: 'Accompagnement',
      title_en: 'Support',
      desc_fr: 'Conseils adaptés selon votre profil',
      desc_en: 'Advice tailored to your profile'
    }
  ];

  return (
    <div className="min-h-[calc(100vh-4rem)]">
      {/* Hero Section */}
      <section className="relative overflow-hidden hero-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 lg:py-32">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left Content */}
            <div className="text-left space-y-8 animate-fade-in-up">
              <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/80 backdrop-blur rounded-full border border-gray-200 shadow-sm">
                <Wind className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium text-gray-700">
                  {language === 'fr' ? 'Offrez-vous la version la plus exigeante de vous-même' : 'Give yourself the most demanding version of yourself'}
                </span>
              </div>

              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 leading-tight">
                {t('heroTitle')}
              </h1>

              <p className="text-lg sm:text-xl text-gray-600 max-w-xl leading-relaxed">
                {t('heroSubtitle')}
              </p>

              <div className="flex flex-col sm:flex-row gap-4">
                <Link to={isAuthenticated ? '/dashboard' : '/register'}>
                  <Button 
                    size="lg" 
                    className="rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all px-8 py-6 text-lg font-semibold group"
                    data-testid="hero-cta-btn"
                  >
                    {t('startNow')}
                    <ArrowRight className="ml-2 w-5 h-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                {!isAuthenticated && (
                  <Link to="/login">
                    <Button 
                      variant="outline" 
                      size="lg" 
                      className="rounded-full border-2 px-8 py-6 text-lg"
                      data-testid="hero-login-btn"
                    >
                      {t('alreadyMember')}
                    </Button>
                  </Link>
                )}
              </div>
            </div>

            {/* Right Image */}
            <div className="relative animate-fade-in-up animate-delay-200">
              <div className="relative rounded-3xl overflow-hidden shadow-2xl">
                <img
                  src="https://customer-assets.emergentagent.com/job_stop-tabac-2/artifacts/tul4dqz9_image.png"
                  alt="ZéphyrBreath - Zéro Tabac"
                  className="w-full h-[400px] lg:h-[500px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent" />
              </div>
              
              {/* Floating Card */}
              <div className="absolute -bottom-6 -left-6 glass-card rounded-2xl p-4 shadow-xl animate-scale-in animate-delay-300">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle2 className="w-6 h-6 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">
                      {language === 'fr' ? 'Test rapide' : 'Quick test'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {language === 'fr' ? '6 questions · 2 min' : '6 questions · 2 min'}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Comment ça marche ?' : 'How does it work?'}
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              {language === 'fr' 
                ? 'Trois étapes simples pour évaluer et comprendre votre dépendance au tabac.'
                : 'Three simple steps to assess and understand your tobacco dependence.'}
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="glass-card rounded-3xl p-8 hover:translate-y-[-4px] transition-all duration-300 group"
                data-testid={`feature-card-${index}`}
              >
                <div className="w-14 h-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                  <feature.icon className="w-7 h-7 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {language === 'fr' ? feature.title_fr : feature.title_en}
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  {language === 'fr' ? feature.desc_fr : feature.desc_en}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#F2F4F1]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
            {language === 'fr' 
              ? 'Prêt à faire le premier pas ?'
              : 'Ready to take the first step?'}
          </h2>
          <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
            {language === 'fr'
              ? 'Créez votre compte gratuitement et découvrez votre niveau de dépendance en quelques minutes.'
              : 'Create your free account and discover your dependency level in minutes.'}
          </p>
          <Link to={isAuthenticated ? '/questionnaire' : '/register'}>
            <Button 
              size="lg" 
              className="rounded-full bg-primary hover:bg-primary/90 shadow-lg hover:shadow-xl transition-all px-10 py-6 text-lg font-semibold"
              data-testid="cta-bottom-btn"
            >
              {t('startNow')}
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
